import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {Upload, CheckCircle2, ChevronDown} from 'lucide-react';
import { usePageSettings } from '@/hooks/usePageSettings';
import { lumi } from '@/lib/lumi';

// ──────────────────────────────────────────
// Tipos
// ──────────────────────────────────────────
type FilterAnswer = '' | 'sim' | 'nao';
type FormStep = 'filter' | 1 | 2 | 3 | 4 | 5 | 6 | 'done';

interface FormData {
  nome: string;
  whatsapp: string;
  email: string;
  areasBloqueios: string[];
  processosAnteriores: string;
  motivacao: string;
}

// ──────────────────────────────────────────
// Constante de paleta
// ──────────────────────────────────────────
const AREAS = [
  'Financeira e profissional',
  'Relacionamentos',
  'Saúde',
  'Autoestima e merecimento',
  'Propósito',
  'Todas as áreas acima',
];

const COUNTRY_CODES = [
  { code: '+55', flag: '🇧🇷', name: 'Brasil' },
  { code: '+1',  flag: '🇺🇸', name: 'EUA' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+44', flag: '🇬🇧', name: 'Reino Unido' },
  { code: '+34', flag: '🇪🇸', name: 'Espanha' },
  { code: '+49', flag: '🇩🇪', name: 'Alemanha' },
  { code: '+33', flag: '🇫🇷', name: 'França' },
  { code: '+39', flag: '🇮🇹', name: 'Itália' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina' },
  { code: '+56', flag: '🇨🇱', name: 'Chile' },
  { code: '+57', flag: '🇨🇴', name: 'Colômbia' },
  { code: '+598', flag: '🇺🇾', name: 'Uruguai' },
];

// ──────────────────────────────────────────
// Componente principal
// ──────────────────────────────────────────
export default function Presentation() {
  const navigate = useNavigate();
  const { getAllSettings, saveSetting } = usePageSettings();
  const [profileImage, setProfileImage] = useState<string>('');
  const [testimonialImages, setTestimonialImages] = useState<string[]>(['', '']);

  // Formulário
  const formRef = useRef<HTMLDivElement>(null);
  const [filterAnswer, setFilterAnswer] = useState<FilterAnswer>('');
  const [formStep, setFormStep] = useState<FormStep>('filter');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryCode, setCountryCode] = useState('+55');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showNotReadyModal, setShowNotReadyModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    whatsapp: '',
    email: '',
    areasBloqueios: [],
    processosAnteriores: '',
    motivacao: '',
  });

  // ── Carregar imagens (1 query, sem bloquear a tela) ──
  useEffect(() => {
    const loadImages = async () => {
      try {
        const all = await getAllSettings();
        if (all['profile_image']) setProfileImage(all['profile_image']);

        const loaded: string[] = [];
        let emptyCount = 0;
        for (let i = 1; i <= 20; i++) {
          const img = all[`testimonial_${i}`];
          if (img) { loaded.push(img); emptyCount = 0; }
          else { emptyCount++; if (emptyCount >= 3) break; }
        }
        if (loaded.length > 0) setTestimonialImages(loaded);
      } catch {
        /* mantém placeholders padrão */
      }
    };
    loadImages();
  }, []);

  // ── Upload imagem de perfil ──
  const handleProfileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const data = ev.target?.result as string;
      setProfileImage(data);
      try { await saveSetting('profile_image', data); } catch { /* silent */ }
    };
    reader.readAsDataURL(file);
  };

  // ── Upload depoimento ──
  const handleTestimonialUpload = (idx: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const data = ev.target?.result as string;
      const next = [...testimonialImages];
      next[idx] = data;
      setTestimonialImages(next);
      try { await saveSetting(`testimonial_${idx + 1}`, data); } catch { /* silent */ }
    };
    reader.readAsDataURL(file);
  };

  // ── Scroll para o formulário ──
  const scrollToForm = () => {
    if (!formRef.current) return;
    const top = formRef.current.getBoundingClientRect().top + window.scrollY - 32;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  // ── Seleção de filtro ──
  const handleFilterSelect = (val: FilterAnswer) => {
    setFilterAnswer(val);
    if (val === 'nao') {
      setShowNotReadyModal(true);
    } else if (val === 'sim') {
      navigate('/inscricao');
    }
  };

  // ── Toggle área de bloqueio ──
  const toggleArea = (area: string) => {
    const current = formData.areasBloqueios;
    if (current.includes(area)) {
      setFormData({ ...formData, areasBloqueios: current.filter(a => a !== area) });
    } else if (current.length < 2) {
      setFormData({ ...formData, areasBloqueios: [...current, area] });
    }
  };

  // ── Avançar etapa ──
  const nextStep = () => {
    if (formStep === 1) setFormStep(2);
    else if (formStep === 2) setFormStep(3);
    else if (formStep === 3) setFormStep(4);
    else if (formStep === 4) setFormStep(5);
    else if (formStep === 5) setFormStep(6);
  };

  // ── Submissão ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const fullWhatsapp = `${countryCode} ${whatsappNumber}`;
      const areasBloqueiosTexto = formData.areasBloqueios.join(', ');
      await lumi.entities.leitura_submissions.create({
        nome: formData.nome,
        whatsapp: fullWhatsapp,
        email: formData.email,
        areasBloqueios: areasBloqueiosTexto,
        processosAnteriores: formData.processosAnteriores,
        motivacao: formData.motivacao,
        status: 'Pendente',
        createdAt: now,
        updatedAt: now,
      });
      // Notificação por e-mail para monicamrsales@gmail.com (não bloqueia o fluxo do usuário em caso de falha)
      fetch('https://api.lumi.new/v1/functions/p418823699841495040/notifyLeituraSubmission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formData.nome,
          email: formData.email,
          whatsapp: fullWhatsapp,
          areasBloqueios: areasBloqueiosTexto,
          processosAnteriores: formData.processosAnteriores,
          motivacao: formData.motivacao,
          createdAt: now,
        }),
      }).catch(() => { /* silencioso: não interrompe o fluxo do formulário */ });
      setFormStep('done');
      window.scrollTo({ top: (formRef.current?.offsetTop || 0) - 100, behavior: 'smooth' });
    } catch {
      alert('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Barra de progresso ──
  const progressSteps = [1, 2, 3, 4, 5, 6];
  const currentStepNum = typeof formStep === 'number' ? formStep : 0;
  const progressPct = currentStepNum > 0 ? Math.round((currentStepNum / 6) * 100) : 0;

  // ── Modal mensagem intermediária ──
  const NotReadyModal = () => (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(13,10,18,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.3s ease-out forwards',
      }}
    >
      <div style={{
        background: '#18131F',
        border: '1px solid rgba(184,141,86,0.28)',
        borderRadius: 8,
        padding: '2.5rem 2rem',
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        {/* Ícone decorativo */}
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(184,141,86,0.1)', border: '1px solid rgba(184,141,86,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
          <span style={{ fontSize: '1.4rem' }}>✦</span>
        </div>

        <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: '2rem' }}>
          Entendemos que esse não é o momento ideal para a Leitura do Campo Vibracional. Mas existe um primeiro passo poderoso e completamente gratuito que pode te ajudar a entender seus bloqueios agora. Quer participar?
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <a
            href="https://elementos.reacendaluz.com.br"
            style={{
              display: 'block',
              background: '#C8923A',
              color: '#FAFCF8',
              fontFamily: 'Lora, Georgia, serif',
              fontWeight: 500,
              fontSize: '1rem',
              padding: '16px 28px',
              borderRadius: 4,
              textDecoration: 'none',
              letterSpacing: '0.02em',
              transition: 'background 0.2s',
            }}
          >
            Sim, quero conhecer meus bloqueios
          </a>
          <button
            onClick={() => { setShowNotReadyModal(false); setFilterAnswer(''); }}
            style={{
              background: 'transparent',
              border: '1px solid rgba(184,141,86,0.4)',
              borderRadius: 4,
              color: '#B88D56',
              fontFamily: 'Lora, Georgia, serif',
              fontSize: '0.95rem',
              padding: '14px 28px',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: '#0D0A12', minHeight: '100vh', color: '#FAFCF8' }}>
      {showNotReadyModal && <NotReadyModal />}

      {/* ═══════════════════════════════════════
          HERO
      ═══════════════════════════════════════ */}
      <section style={{ background: 'linear-gradient(180deg, #1A1228 0%, #0D0A12 100%)' }} className="relative overflow-hidden">
        {/* Decoração sutil */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(ellipse 60% 40% at 50% 20%, rgba(184,141,86,0.08) 0%, transparent 70%)',
        }} />
        <div className="relative max-w-3xl mx-auto px-6 py-20 md:py-32 text-center">
          <p style={{ color: '#B88D56', fontFamily: 'Lora, Georgia, serif', letterSpacing: '0.18em', fontSize: '0.85rem', textTransform: 'uppercase' }} className="mb-6 tracking-widest">
            Reacenda Luz
          </p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 600 }} className="mb-6">
            Leitura do Campo Vibracional
          </h1>
          <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#EFC98A', fontSize: '1.3rem', lineHeight: 1.6, fontWeight: 400 }} className="mb-5 max-w-2xl mx-auto">
            O que está travando a sua vida tem uma raiz. E ela pode ser revelada.
          </p>
          <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '1.02rem', lineHeight: 1.7, fontWeight: 400, fontStyle: 'italic' }} className="mb-8 max-w-2xl mx-auto">
            ✦ Este processo é exclusivo para mulheres.
          </p>
          <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.08rem', lineHeight: 1.85, fontWeight: 400 }} className="mb-5 max-w-2xl mx-auto">
            A Leitura do Campo Vibracional não é uma sessão. Não é uma leitura rápida para curiosas. É um processo profundo de autoconhecimento e libertação que dura 30 dias.
          </p>
          <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.88)', fontSize: '1.05rem', lineHeight: 1.85, fontWeight: 400 }} className="mb-10 max-w-2xl mx-auto">
            Esse processo é para quem está disposta a mergulhar de verdade no que está enraizado nas crenças, nas dores emocionais e nos padrões invisíveis que impedem a evolução em todas as áreas da vida. Se você busca uma resposta superficial, esse não é o processo certo. Se você está pronta para ir à raiz de uma vez, continue lendo.
          </p>
          <button
            onClick={scrollToForm}
            className="btn-cta-hover"
            style={{
              background: '#C8923A',
              color: '#FAFCF8',
              fontFamily: 'Lora, Georgia, serif',
              fontWeight: 500,
              letterSpacing: '0.02em',
              border: 'none',
              cursor: 'pointer',
              padding: '18px 40px',
              borderRadius: '4px',
              fontSize: '1.1rem',
              transition: 'background 0.2s',
            }}
          >
            Quero fazer minha Leitura do Campo Vibracional
          </button>
          <div className="mt-10 flex justify-center">
            <ChevronDown style={{ color: '#B88D56', opacity: 0.6, width: 28, height: 28 }} />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          BLOCO 2 — O QUE É
      ═══════════════════════════════════════ */}
      <section className="max-w-2xl mx-auto px-6 py-16 md:py-20 text-center">
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.15rem', lineHeight: 1.9, marginBottom: '1.5rem' }}>
          Se você já fez terapia, cursos, constelação ou processos espirituais e ainda sente que algo invisível continua no mesmo lugar existe uma razão que ainda não foi acessada.
        </p>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.15rem', lineHeight: 1.9, marginBottom: '1.5rem' }}>
          Não é falta de fé. Não é falta de esforço. É um padrão que opera no seu campo vibracional em silêncio e que processos convencionais não conseguem tocar.
        </p>
        <div style={{ borderLeft: '2px solid #B88D56', paddingLeft: '1.5rem', margin: '2rem auto', maxWidth: '520px', textAlign: 'left' }}>
          <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#EFC98A', fontSize: '1.35rem', fontStyle: 'italic', lineHeight: 1.7 }}>
            A Leitura do Campo Vibracional revela o que está invisível e libera o que ainda prende.
          </p>
        </div>
      </section>

      {/* Divisor */}
      <div style={{ width: '48px', height: '1px', background: '#B88D56', margin: '0 auto 0' }} />

      {/* ═══════════════════════════════════════
          BLOCO 3 — PARA QUEM É
      ═══════════════════════════════════════ */}
      <section className="max-w-2xl mx-auto px-6 py-16 md:py-20">
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, textAlign: 'center', marginBottom: '2.5rem' }}>
          Este processo é para você se:
        </h2>
        <ul className="space-y-5">
          {[
            'Já investiu em terapia, constelação, coaching ou práticas espirituais e algo ainda permanece intocado.',
            'Tem uma vida construída mas sente que está vivendo muito abaixo do que deveria.',
            'Não quer mais aliviar sintomas. Está pronta para ir à raiz de uma vez.',
            'Sente que carrega padrões que se repetem há anos ou há gerações.',
            'Está disposta a se comprometer com uma transformação real e profunda.',
          ].map((item, i) => (
            <li key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <span style={{ color: '#B88D56', fontSize: '1.1rem', lineHeight: 1.8, flexShrink: 0 }}>✦</span>
              <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.1rem', lineHeight: 1.8 }}>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Divisor */}
      <div style={{ width: '48px', height: '1px', background: '#B88D56', margin: '0 auto' }} />

      {/* ═══════════════════════════════════════
          BLOCO 4 — ANTES E DEPOIS
      ═══════════════════════════════════════ */}
      <section className="max-w-4xl mx-auto px-6 py-16 md:py-20">
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, textAlign: 'center', marginBottom: '3rem' }}>
          O que muda depois da Leitura
        </h2>
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Antes */}
          <div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#EFC98A', fontWeight: 500, marginBottom: '1.5rem', textAlign: 'center' }}>
              Antes
            </h3>
            <ul className="space-y-3">
              {[
                'Ciclos que se repetem sem explicação.',
                'Sensação de estar presa mesmo quando avança.',
                'Padrões que voltam sempre independente do que tenta.',
                'Algo invisível que nenhum processo anterior tocou.',
                'Cansaço de tentar e não chegar à raiz.',
              ].map((t, i) => (
                <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <span style={{ color: '#EFC98A', opacity: 0.5, flexShrink: 0, marginTop: '0.25rem' }}>—</span>
                  <span style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.75)', fontSize: '1.05rem', lineHeight: 1.8 }}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* Depois */}
          <div style={{ borderLeft: '1px solid rgba(184,141,86,0.3)', paddingLeft: '2rem' }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#B88D56', fontWeight: 500, marginBottom: '1.5rem', textAlign: 'center' }}>
              Depois
            </h3>
            <ul className="space-y-3">
              {[
                'Clareza do que estava na raiz de tudo.',
                'Leveza de ter nomeado o que antes era invisível.',
                'Libertação de padrões que operavam em silêncio há anos.',
                'Direção real para o que vem a seguir.',
                'Certeza de que desta vez chegou à raiz de verdade.',
              ].map((t, i) => (
                <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <span style={{ color: '#B88D56', flexShrink: 0, marginTop: '0.25rem' }}>✦</span>
                  <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.05rem', lineHeight: 1.8 }}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Divisor */}
      <div style={{ width: '48px', height: '1px', background: '#B88D56', margin: '0 auto' }} />

      {/* ═══════════════════════════════════════
          BLOCO 5 — QUEM CONDUZ
      ═══════════════════════════════════════ */}
      <section className="max-w-2xl mx-auto px-6 py-16 md:py-20 text-center">
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, marginBottom: '2.5rem' }}>
          Quem conduz
        </h2>
        {/* Foto */}
        <div className="flex justify-center mb-6">
          <div style={{ width: 200, height: 250, borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(184,141,86,0.3)', position: 'relative' }}>
            {profileImage ? (
              <img src={profileImage} alt="Mônica Maria" width={200} height={250} loading="eager" className="w-full h-full object-cover" style={{ display: 'block' }} />
            ) : (
              <label style={{ width: '100%', height: '100%', background: 'rgba(184,141,86,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 8 }}>
                <Upload style={{ width: 28, height: 28, color: '#B88D56' }} />
                <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.8rem', textAlign: 'center', padding: '0 12px' }}>Upload da foto</span>
                <input type="file" accept="image/*" onChange={handleProfileUpload} className="hidden" />
              </label>
            )}
          </div>
        </div>
        <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#EFC98A', fontWeight: 600, marginBottom: '0.5rem' }}>
          Mônica Maria
        </h3>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.95rem', marginBottom: '1.5rem', letterSpacing: '0.04em' }}>
          Terapeuta Vibracional e Especialista em Leitura do Campo Vibracional
        </p>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.85)', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: '1rem' }}>
          Desde 2017 conduzo pessoas que já percorreram muitos caminhos de cura, terapia, constelação, coaching e práticas espirituais e que ainda carregam padrões repetitivos e limitantes.
        </p>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.85)', fontSize: '1.05rem', lineHeight: 1.9 }}>
          Meu trabalho é revelar o que está invisível e liberar o que ainda prende.
        </p>
      </section>

      {/* Divisor */}
      <div style={{ width: '48px', height: '1px', background: '#B88D56', margin: '0 auto' }} />

      {/* ═══════════════════════════════════════
          BLOCO 6 — DEPOIMENTOS
      ═══════════════════════════════════════ */}
      <section className="max-w-2xl mx-auto px-6 py-16 md:py-20">
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, textAlign: 'center', marginBottom: '2.5rem' }}>
          Experiências reais
        </h2>
        <div className="space-y-6">
          {testimonialImages.map((image, idx) => (
            <div key={idx}>
              {image ? (
                <img
                  src={image}
                  alt={`Depoimento ${idx + 1}`}
                  width={768}
                  height={1024}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.4)', border: '1px solid rgba(184,141,86,0.2)' }}
                />
              ) : (
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <div style={{ border: '1px dashed rgba(184,141,86,0.4)', borderRadius: 8, padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, background: 'rgba(184,141,86,0.04)' }}>
                    <Upload style={{ width: 24, height: 24, color: '#B88D56', opacity: 0.7 }} />
                    <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.9rem', opacity: 0.8 }}>Adicionar depoimento {idx + 1}</span>
                  </div>
                  <input type="file" accept="image/*" onChange={handleTestimonialUpload(idx)} className="hidden" />
                </label>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Divisor */}
      <div style={{ width: '48px', height: '1px', background: '#B88D56', margin: '0 auto' }} />

      {/* ═══════════════════════════════════════
          BLOCO 7 — INVESTIMENTO + CTA
      ═══════════════════════════════════════ */}
      <section className="max-w-2xl mx-auto px-6 py-16 md:py-20 text-center">
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.85rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '1.2rem' }}>
          Atenção
        </p>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.7)', fontSize: '0.95rem', marginBottom: '2.5rem' }}>
          As vagas são limitadas e os atendimentos são agendados individualmente.
        </p>
        <div style={{ background: 'rgba(184,141,86,0.07)', border: '1px solid rgba(184,141,86,0.2)', borderRadius: 6, padding: '1.5rem 2rem', marginBottom: '2.5rem', textAlign: 'left' }}>
          <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.85)', fontSize: '1rem', lineHeight: 1.85 }}>
            O formulário abaixo é o primeiro passo. Após receber suas respostas entrarei em contato para uma conversa onde você entende detalhadamente como funciona o processo e decidimos juntas se esse é o momento certo para você.
          </p>
        </div>
        <button
          onClick={scrollToForm}
          className="btn-cta-hover"
          style={{
            background: '#C8923A',
            color: '#FAFCF8',
            fontFamily: 'Lora, Georgia, serif',
            fontWeight: 500,
            letterSpacing: '0.02em',
            border: 'none',
            cursor: 'pointer',
            padding: '18px 40px',
            borderRadius: '4px',
            fontSize: '1.1rem',
            transition: 'background 0.2s',
          }}
        >
          Quero fazer minha Leitura do Campo Vibracional
        </button>
      </section>

      {/* ═══════════════════════════════════════
          BLOCO 8 — FORMULÁRIO
      ═══════════════════════════════════════ */}
      <div ref={formRef} style={{ background: 'rgba(184,141,86,0.05)', borderTop: '1px solid rgba(184,141,86,0.15)', borderBottom: '1px solid rgba(184,141,86,0.15)', scrollMarginTop: '32px' }}>
        <section className="max-w-xl mx-auto px-6 py-16 md:py-20">

          {/* ── FILTRO ── */}
          {formStep === 'filter' && (
            <div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, textAlign: 'center', marginBottom: '1.5rem' }}>
                Antes de preencher o formulário
              </h2>
              <div style={{ background: 'rgba(184,141,86,0.08)', border: '1px solid rgba(184,141,86,0.25)', borderRadius: 6, padding: '1.5rem 2rem', marginBottom: '2rem' }}>
                <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.05rem', lineHeight: 1.85, textAlign: 'center', marginBottom: '1rem' }}>
                  Antes de preencher o formulário, é importante que você saiba: a Leitura do Campo Vibracional é um processo de alto comprometimento. Não é superficial. Não é feita em um único atendimento. É uma jornada de libertação profunda que envolve várias etapas e dura 30 dias.
                </p>
                <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1.05rem', lineHeight: 1.85, textAlign: 'center' }}>
                  Após receber suas respostas entrarei em contato para uma conversa onde você entende detalhadamente como funciona o processo e decidimos juntas se esse é o momento certo para você.
                </p>
              </div>
              <div className="space-y-3">
                {([
                  { val: 'sim' as FilterAnswer, label: 'Sim, se fizer sentido estou pronta para investir' },
                  { val: 'nao' as FilterAnswer, label: 'Ainda não é o momento financeiro ideal' },
                ]).map(({ val, label }) => (
                  <button
                    key={val}
                    onClick={() => handleFilterSelect(val)}
                    style={{
                      width: '100%',
                      background: filterAnswer === val ? 'rgba(200,146,58,0.2)' : 'rgba(184,141,86,0.06)',
                      border: `1px solid ${filterAnswer === val ? '#C8923A' : 'rgba(184,141,86,0.25)'}`,
                      borderRadius: 4,
                      padding: '1rem 1.25rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'background 0.2s, border-color 0.2s',
                    }}
                  >
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                      border: `2px solid ${filterAnswer === val ? '#C8923A' : 'rgba(184,141,86,0.5)'}`,
                      background: filterAnswer === val ? '#C8923A' : 'transparent',
                      transition: 'background 0.2s, border-color 0.2s',
                    }} />
                    <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1rem', lineHeight: 1.6 }}>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── FORMULÁRIO QUALIFICADO ── */}
          {typeof formStep === 'number' && formStep >= 1 && formStep <= 6 && (
            <form onSubmit={formStep === 6 ? handleSubmit : (e) => { e.preventDefault(); nextStep(); }} className="animate-fadeIn">
              {/* Barra de progresso */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.8rem' }}>Passo {currentStepNum} de 6</span>
                  <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.8rem' }}>{progressPct}%</span>
                </div>
                <div style={{ height: 3, background: 'rgba(184,141,86,0.2)', borderRadius: 99 }}>
                  <div style={{ height: '100%', width: `${progressPct}%`, background: '#C8923A', borderRadius: 99, transition: 'width 0.4s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem' }}>
                  {progressSteps.map(s => (
                    <div key={s} style={{ width: 8, height: 8, borderRadius: '50%', background: s <= currentStepNum ? '#C8923A' : 'rgba(184,141,86,0.25)', transition: 'background 0.3s' }} />
                  ))}
                </div>
              </div>

              {/* Step 1 — Nome */}
              {formStep === 1 && (
                <div className="animate-fadeIn">
                  <label style={labelStyle}>Qual é o seu nome completo? *</label>
                  <input
                    type="text" required autoFocus
                    value={formData.nome}
                    onChange={e => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Seu nome completo"
                    style={inputStyle}
                  />
                </div>
              )}

              {/* Step 2 — WhatsApp */}
              {formStep === 2 && (
                <div className="animate-fadeIn">
                  <label style={labelStyle}>Qual é o seu WhatsApp com código do país? *</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      style={{ ...inputStyle, width: 'auto', flexShrink: 0, paddingRight: '0.75rem', cursor: 'pointer' }}
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel" required autoFocus
                      value={whatsappNumber}
                      onChange={e => setWhatsappNumber(e.target.value)}
                      placeholder="(11) 99999-9999"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                  </div>
                </div>
              )}

              {/* Step 3 — Email */}
              {formStep === 3 && (
                <div className="animate-fadeIn">
                  <label style={labelStyle}>Qual é o seu email? *</label>
                  <input
                    type="email" required autoFocus
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    style={inputStyle}
                  />
                </div>
              )}

              {/* Step 4 — Áreas (múltipla escolha até 2) */}
              {formStep === 4 && (
                <div className="animate-fadeIn">
                  <label style={labelStyle}>Qual área da sua vida está com mais bloqueios agora? (escolha até 2) *</label>
                  <div className="space-y-3 mt-3">
                    {AREAS.map(area => {
                      const selected = formData.areasBloqueios.includes(area);
                      const disabled = !selected && formData.areasBloqueios.length >= 2;
                      return (
                        <button
                          key={area}
                          type="button"
                          disabled={disabled}
                          onClick={() => toggleArea(area)}
                          style={{
                            width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.75rem',
                            background: selected ? 'rgba(200,146,58,0.18)' : 'rgba(184,141,86,0.05)',
                            border: `1px solid ${selected ? '#C8923A' : 'rgba(184,141,86,0.2)'}`,
                            borderRadius: 4, padding: '0.9rem 1.1rem', cursor: disabled ? 'default' : 'pointer',
                            opacity: disabled ? 0.4 : 1, transition: 'all 0.2s',
                          }}
                        >
                          {selected
                            ? <CheckCircle2 style={{ width: 18, height: 18, color: '#C8923A', flexShrink: 0 }} />
                            : <span style={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid rgba(184,141,86,0.45)', display: 'inline-block', flexShrink: 0 }} />
                          }
                          <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1rem' }}>{area}</span>
                        </button>
                      );
                    })}
                  </div>
                  {formData.areasBloqueios.length === 0 && (
                    <p style={{ color: '#B88D56', fontSize: '0.85rem', marginTop: '0.75rem', fontFamily: 'Lora, Georgia, serif' }}>Selecione pelo menos 1 área.</p>
                  )}
                </div>
              )}

              {/* Step 5 — Processos anteriores */}
              {formStep === 5 && (
                <div className="animate-fadeIn">
                  <label style={labelStyle}>Você já fez algum processo de terapia, cura ou autoconhecimento antes? O que funcionou e o que ainda não mudou? *</label>
                  <textarea
                    required autoFocus rows={5}
                    value={formData.processosAnteriores}
                    onChange={e => setFormData({ ...formData, processosAnteriores: e.target.value })}
                    placeholder="Descreva com honestidade..."
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                  />
                </div>
              )}

              {/* Step 6 — Motivação */}
              {formStep === 6 && (
                <div className="animate-fadeIn">
                  <label style={labelStyle}>O que te trouxe até aqui? O que está acontecendo na sua vida agora que fez você buscar esse processo? *</label>
                  <textarea
                    required autoFocus rows={5}
                    value={formData.motivacao}
                    onChange={e => setFormData({ ...formData, motivacao: e.target.value })}
                    placeholder="Compartilhe com honestidade..."
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                  />
                  <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.5)', fontSize: '0.85rem', marginTop: '1rem', lineHeight: 1.7, textAlign: 'center' }}>
                    Seus dados são confidenciais e não serão compartilhados.
                  </p>
                </div>
              )}

              {/* Botão avançar / enviar */}
              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (formStep === 1 && !formData.nome.trim()) ||
                    (formStep === 2 && !whatsappNumber.trim()) ||
                    (formStep === 3 && !formData.email.trim()) ||
                    (formStep === 4 && formData.areasBloqueios.length === 0) ||
                    (formStep === 5 && !formData.processosAnteriores.trim()) ||
                    (formStep === 6 && !formData.motivacao.trim())
                  }
                  style={{
                    background: '#C8923A',
                    color: '#FAFCF8',
                    fontFamily: 'Lora, Georgia, serif',
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '16px 36px',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    opacity: isSubmitting ? 0.6 : 1,
                    transition: 'background 0.2s',
                  }}
                >
                  {isSubmitting ? 'Enviando...' : formStep === 6 ? 'Enviar minhas respostas' : 'Continuar →'}
                </button>
              </div>
            </form>
          )}

          {/* ── CONFIRMAÇÃO ── */}
          {formStep === 'done' && (
            <div className="animate-fadeIn text-center">
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(184,141,86,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                <CheckCircle2 style={{ width: 28, height: 28, color: '#B88D56' }} />
              </div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, marginBottom: '1.5rem' }}>
                Suas respostas chegaram.
              </h2>
              <div style={{ border: '1px solid rgba(184,141,86,0.25)', borderRadius: 6, padding: '1.75rem 2rem', marginBottom: '1.5rem', textAlign: 'left' }}>
                <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.9)', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: '1rem' }}>
                  Obrigada por compartilhar com tanta honestidade. Lerei tudo com cuidado e presença e entrarei em contato em até 48 horas para conversarmos.
                </p>
                <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#EFC98A', fontSize: '1.1rem', fontStyle: 'italic' }}>
                  Com amor e presença, Mônica — Reacenda Luz
                </p>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ═══════════════════════════════════════
          RODAPÉ
      ═══════════════════════════════════════ */}
      <footer style={{ borderTop: '1px solid rgba(184,141,86,0.1)', padding: '2rem 1.5rem', textAlign: 'center' }}>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(184,141,86,0.5)', fontSize: '0.85rem' }}>
          © Reacenda Luz · Todos os direitos reservados
        </p>
      </footer>
    </div>
  );
}

// ──────────────────────────────────────────
// Estilos inline reutilizáveis
// ──────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Lora, Georgia, serif',
  color: '#FAFCF8',
  fontSize: '1.05rem',
  lineHeight: 1.7,
  marginBottom: '1rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(184,141,86,0.06)',
  border: '1px solid rgba(184,141,86,0.3)',
  borderRadius: 4,
  padding: '14px 16px',
  fontFamily: 'Lora, Georgia, serif',
  color: '#FAFCF8',
  fontSize: '1rem',
  outline: 'none',
  boxSizing: 'border-box',
};
